import React, { memo, ReactEventHandler } from 'react';
import {
    ChildExamPointsStatusIcon,
    ExamPointsRightArrowIcon,
    ChildExamPointNoStudyIcon,
    ChildExamPointsUncompletedIcon,
    ChildExamPointsCompleteIcon,
} from '@/svgs';
import './childExamPoint.less';
import { useAppStore } from '@/store';
import { IHomePageExamSyllabusContentItem } from '@/types';
import classNames from 'classnames';
import globalStore from '@/store/global';
import { useAuthContext } from '@/pages/prediction-score/context/AuthContext';
import { useCommonInfoContext } from '@/pages/prediction-score/context/CommonInfoProvider';
import { createPredictScoreExamPointPractice } from '@/apis';
import { bindEvents } from '@/utils/sensors';

interface IProps {
    data: IHomePageExamSyllabusContentItem;
    isShowIconLine?:
        | false
        | {
              prefix: boolean;
              suffix: boolean;
          };
}

const ChildExamPoint: React.FC<IProps> = ({ data, isShowIconLine = false }) => {
    const {
        themeStore: { theme },
    } = useAppStore();
    const {
        commonInfo: { projectId, subjectId, examSeasonId, sourceFromType, businessParam },
    } = useCommonInfoContext();
    const { withAuthCheck } = useAuthContext();

    const handleGoToDetail = withAuthCheck(
        (event: React.MouseEvent) => {
            setTimeout(() => {
                globalStore.publish('main-app', {
                    type: 'gotoDetail',
                    params: {
                        examSeasonId,
                        examSyllabusId: data.examSyllabusId,
                        knowledgeId: data.knowledgeId,
                        sectionId: data.contentId,
                        goalScore: data.goalScore,
                    },
                });
            }, 80);
        },
        () =>
            bindEvents(
                'Click_KnowledgePoint',
                {
                    name: '点击考点详情',
                    project_id: projectId,
                    subject_id: subjectId,
                    KnowledgeNode_name: data.title,
                },
                80
            )
    );

    const handleGoToPractice = withAuthCheck(async (event: React.MouseEvent) => {
        event.stopPropagation();
        // 继续做题
        if (data.submitStatus === 0 && data.paperDataLogId) {
            globalStore.publish('main-app', {
                type: 'gotoPractice',
                params: {
                    paperDataLogId: data.paperDataLogId,
                    knowledgeId: data.knowledgeId,
                },
            });
            return;
        }
        // 未做练习、已交卷
        const response = await createPredictScoreExamPointPractice({
            projectId,
            subjectId,
            examSeasonId,
            sourceFromType,
            knowledgeId: data.knowledgeId,
            knowledgeTitle: data.title,
            businessParam: businessParam ? { ...businessParam, knowledgeId: data.knowledgeId } : undefined,
        });
        if (response.status === 0 && response.result?.paperDataLogId) {
            globalStore.publish('main-app', {
                type: 'gotoPractice',
                params: {
                    paperDataLogId: response.result.paperDataLogId,
                    knowledgeId: data.knowledgeId,
                },
            });
        }
    });

    return (
        <div className="sub-tiku__child-exam-point">
            <div
                className={classNames('sub-tiku__child-exam-point-icon', {
                    prefix: isShowIconLine && isShowIconLine.prefix,
                    suffix: isShowIconLine && isShowIconLine.suffix,
                })}
            >
                {data.learnStatus === 0 ? (
                    <ChildExamPointNoStudyIcon />
                ) : data.learnStatus === 2 ? (
                    <ChildExamPointsUncompletedIcon color={theme.mainColor} />
                ) : data.learnStatus === 1 ? (
                    <ChildExamPointsCompleteIcon />
                ) : null}
            </div>
            <div
                className={classNames('sub-tiku__child-exam-point-main', {
                    uncompleted: data.learnStatus === 2,
                    completed: data.learnStatus === 1,
                })}
            >
                <div className="sub-tiku__child-exam-point-meta">
                    <div className="sub-tiku__child-exam-point-meta-content">
                        <div className="sub-tiku__child-exam-point-meta-title">{data.title}</div>
                        <div className="sub-tiku__child-exam-point-meta-action" onClick={handleGoToDetail}>
                            <ExamPointsRightArrowIcon />
                        </div>
                    </div>
                </div>
                <div
                    className={classNames('sub-tiku__child-exam-point-detail', {
                        'sub-tiku__last-resource': data.lastTimeStatus === 1,
                    })}
                    onClick={handleGoToPractice}
                >
                    {data.learnStatus === 1 ? (
                        <>
                            <div className="sub-tiku__status-icon">
                                <img src="https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202503/03/824e9_20250303152739.png" />
                            </div>
                            <div className="sub-tiku__study-score completed">
                                已获得<span className="sub-tiku__study-score-em">{data.predictionScore}</span>分
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="sub-tiku__status-icon">
                                <ChildExamPointsStatusIcon color={theme.mainColor} />
                            </div>
                            <div className="sub-tiku__study-score">
                                <span className="sub-tiku__study-score-em">{data.predictionScore ?? '--'}</span>/{data.goalScore}分
                            </div>
                        </>
                    )}
                    <div className="sub-tiku__total-score">总{data.totalScore}分</div>
                    <div className="sub-tiku__mr-auto"></div>
                    {data.lastTimeStatus === 1 ? (
                        <div className="sub-tiku__study-tag">最近一次</div>
                    ) : data.submitStatus === 0 ? (
                        <div className="sub-tiku__study-tag">未交卷</div>
                    ) : null}
                    <div className="sub-tiku__question-count">
                        {data.finishedQuestionCount}/{data.totalQuestionCount}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(ChildExamPoint);
